import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { api, Report } from '../../services/api'
import {
  ChevronLeft,
  Upload,
  FileText,
  Trash2,
  Edit2,
  Download,
  Loader2,
  X,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react'
import { toast } from 'sonner'

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export default function ReportsManager() {
  const { token } = useAuth()
  const [reports, setReports] = useState<Report[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [editingReport, setEditingReport] = useState<Report | null>(null)
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const [uploadForm, setUploadForm] = useState({
    title: 'Relatório da Escuta Social',
    description: '',
    file: null as File | null
  })

  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    isPublished: true
  })

  useEffect(() => {
    loadReports()
  }, [token])

  const loadReports = async () => {
    if (!token) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    try {
      const data = await api.getAdminReports(token)
      setReports(data)
    } catch (error) {
      console.error('Erro ao carregar relatórios:', error)
      toast.error('Erro ao carregar relatórios')
    } finally {
      setIsLoading(false)
    }
  }

  const currentReport = reports.find(r => r.isPublished)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Apenas arquivos PDF são permitidos')
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Máximo 10MB')
        return
      }
      setUploadForm(prev => ({ ...prev, file }))
    }
  }

  const handleUpload = async () => {
    if (!token) {
      toast.error('Sessão expirada. Faça login novamente.')
      return
    }

    if (!uploadForm.title.trim()) {
      toast.error('Título é obrigatório')
      return
    }
    if (!uploadForm.file) {
      toast.error('Selecione um arquivo PDF')
      return
    }

    setUploading(true)
    try {
      await api.uploadReport(token, {
        title: uploadForm.title,
        description: uploadForm.description,
        file: uploadForm.file
      })
      toast.success('Relatório publicado com sucesso!')
      setUploadForm({ title: 'Relatório da Escuta Social', description: '', file: null })
      if (fileInputRef.current) fileInputRef.current.value = ''
      await loadReports()
    } catch (error: any) {
      toast.error(error.message || 'Erro ao fazer upload')
    } finally {
      setUploading(false)
    }
  }

  const handleEdit = async () => {
    if (!token || !editingReport) {
      toast.error('Erro ao atualizar relatório')
      return
    }

    if (!editForm.title.trim()) {
      toast.error('Título é obrigatório')
      return
    }

    setUploading(true)
    try {
      await api.updateReport(token, editingReport.id, {
        title: editForm.title,
        description: editForm.description,
        isPublished: editForm.isPublished
      })
      toast.success('Relatório atualizado com sucesso!')
      setEditingReport(null)
      await loadReports()
    } catch (error: any) {
      toast.error(error.message || 'Erro ao atualizar relatório')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (report: Report) => {
    if (!token) {
      toast.error('Sessão expirada. Faça login novamente.')
      return
    }

    if (!confirm(`Tem certeza que deseja excluir "${report.title}"?`)) return

    setDeletingId(report.id)
    try {
      await api.deleteReport(token, report.id)
      toast.success('Relatório removido com sucesso!')
      await loadReports()
    } catch (error: any) {
      toast.error(error.message || 'Erro ao remover relatório')
    } finally {
      setDeletingId(null)
    }
  }

  const handleDownload = async (report: Report) => {
    try {
      await api.downloadReport(report.id, report.originalName)
      toast.success(`Download iniciado: ${report.title}`)
    } catch (error) {
      toast.error('Erro ao baixar o arquivo')
    }
  }

  const openEditModal = (report: Report) => {
    setEditingReport(report)
    setEditForm({
      title: report.title,
      description: report.description || '',
      isPublished: report.isPublished
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#51A64D] mx-auto mb-4"></div>
          <p className="text-gray-500">Carregando relatórios...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <a href="/admin/dashboard" className="text-gray-400 hover:text-gray-600">
            <ChevronLeft size={20} />
          </a>
          <div>
            <h1 className="text-xl font-serif font-bold text-gray-900">Gerenciar Relatórios</h1>
            <p className="text-sm text-gray-500">Publique e gerencie o relatório da Escuta Social</p>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-3xl mx-auto space-y-6">
        {/* Status atual */}
        {currentReport ? (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle2 size={20} className="text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900">{currentReport.title}</p>
                {currentReport.description && (
                  <p className="text-sm text-gray-500 mt-1">{currentReport.description}</p>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  Publicado em {formatDate(currentReport.createdAt)} · {formatFileSize(currentReport.fileSize)}
                </p>
              </div>
              <button
                onClick={() => handleDownload(currentReport)}
                className="p-2 text-gray-400 hover:text-[#51A64D] transition-colors flex-shrink-0"
                title="Baixar"
              >
                <Download size={18} />
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Nenhum relatório publicado</p>
              <p className="text-sm text-amber-700 mt-1">
                Ainda não há um relatório da Escuta Social disponível para download. Faça upload de um PDF para publicar.
              </p>
            </div>
          </div>
        )}

        {/* Formulário de publicação */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h2 className="font-serif font-bold text-lg text-gray-900 mb-5">Publicar Relatório</h2>
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Título</label>
              <input
                type="text"
                value={uploadForm.title}
                onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Descrição (opcional)</label>
              <textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20 min-h-[80px]"
                placeholder="Descrição do relatório..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Arquivo PDF <span className="text-red-500">*</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileChange}
                className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#51A64D] file:text-white hover:file:bg-[#458f3f] transition-colors"
              />
              <p className="text-xs text-gray-400 mt-1">PDF até 10MB</p>
              {uploadForm.file && (
                <p className="text-sm text-green-600 mt-2 flex items-center gap-1.5">
                  <CheckCircle2 size={14} />
                  Arquivo selecionado: {uploadForm.file.name}
                </p>
              )}
            </div>
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#2C5B8A] text-white text-sm font-medium rounded-lg hover:bg-[#234a70] transition-colors disabled:opacity-50"
            >
              {uploading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Publicar Relatório
                </>
              )}
            </button>
          </div>
        </div>

        {/* Histórico */}
        {reports.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="font-serif font-bold text-lg text-gray-900">Histórico de Relatórios</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Relatório</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Data</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Tamanho</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <FileText size={18} className="text-[#51A64D]" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{report.title}</div>
                            {report.description && (
                              <div className="text-xs text-gray-400 mt-0.5">{report.description}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(report.createdAt)}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatFileSize(report.fileSize)}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          report.isPublished
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {report.isPublished ? (
                            <>
                              <Eye size={10} />
                              Publicado
                            </>
                          ) : (
                            <>
                              <EyeOff size={10} />
                              Oculto
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDownload(report)}
                            className="p-1.5 text-gray-400 hover:text-[#51A64D] transition-colors"
                            title="Baixar"
                          >
                            <Download size={16} />
                          </button>
                          <button
                            onClick={() => openEditModal(report)}
                            className="p-1.5 text-gray-400 hover:text-amber-600 transition-colors"
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(report)}
                            disabled={deletingId === report.id}
                            className="p-1.5 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                            title="Excluir"
                          >
                            {deletingId === report.id ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Trash2 size={16} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Edição */}
      {editingReport && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h3 className="font-serif font-bold text-lg text-gray-900">Editar Relatório</h3>
              <button onClick={() => setEditingReport(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Título <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Descrição</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20 min-h-[80px]"
                />
              </div>
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editForm.isPublished}
                    onChange={(e) => setEditForm(prev => ({ ...prev, isPublished: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300 text-[#51A64D] focus:ring-[#51A64D]"
                  />
                  <span className="text-sm text-gray-700">Relatório publicado (visível no site)</span>
                </label>
              </div>
            </div>
            <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setEditingReport(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleEdit}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 bg-[#51A64D] text-white text-sm rounded-lg hover:bg-[#458f3f] transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
